import React from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import { GeoAlt } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../contexts/LocalizationContext';
import './AddressModal.scss';

interface AddressModalProps {
  show: boolean;
  onHide: () => void;
  selectedAddressOption: string;
  temporaryAddress: any;
  recommendedShippingAddress: any;
  addressValidationResult: any;
  formShippingAddress: any;
  onAddressOptionChange: (e: React.ChangeEvent<HTMLInputElement>, option: string) => void;
  onShippingAddressUpdate: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirmAddress: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const AddressModal: React.FC<AddressModalProps> = ({
  show,
  onHide,
  selectedAddressOption,
  temporaryAddress,
  recommendedShippingAddress,
  addressValidationResult,
  formShippingAddress,
  onAddressOptionChange,
  onShippingAddressUpdate,
  onConfirmAddress
}) => {
  const { t } = useTranslation();
  const { locale } = useLocalization();

  return (
    <Modal
      className="address-modal"
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      aria-labelledby="address-modal-title"
      aria-describedby="address-modal-desc"
    >
      <Modal.Header closeButton={false}>
        <Modal.Title id="address-modal-title">
          <GeoAlt className="me-2" />
          {t('CONFIRM_ADDRESS')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="address-modal-desc">
        <div className="address-options">
          <fieldset aria-label={t("ADDRESS_OPTIONS")}>
            <legend className="visually-hidden">{t("ADDRESS_OPTIONS")}</legend>
            <div className="radioGroup">
              <Form.Check
                type="radio"
                name="addressOption"
                id="yourAddress"
                checked={selectedAddressOption === "yourAddress"}
                onChange={(e) => onAddressOptionChange(e, "yourAddress")}
                label={
                  <span className="labelWrapper">
                    <span className="radioLabels">
                      <strong>{t("YOUR_ADDRESS")}</strong>
                      {temporaryAddress?.line1 && <>{temporaryAddress?.line1}</>}
                      {temporaryAddress?.line2 && <>,&#160;{temporaryAddress?.line2}</>}
                      {temporaryAddress?.city && <><br />{temporaryAddress?.city}</>}
                      {temporaryAddress?.countrySubdivision && <>,&#160;{temporaryAddress?.countrySubdivision}</>}
                      {temporaryAddress?.postalCode && <>&#160;{temporaryAddress?.postalCode}</>}
                      {temporaryAddress?.countryName && <><br />{temporaryAddress?.countryName}</>}
                    </span>
                  </span>
                }
              />
              {recommendedShippingAddress && (
                <div>
                  <Form.Check
                    type="radio"
                    name="addressOption"
                    id="recommendedAddress"
                    checked={selectedAddressOption === "recommendedAddress"}
                    onChange={(e) =>
                      onAddressOptionChange(e, "recommendedAddress")
                    }
                    label={
                      <span className="labelWrapper">
                        <span className="radioLabels">
                          <strong>{t("RECOMMENDED_ADDRESS")}</strong>
                          {recommendedShippingAddress.address && <>{recommendedShippingAddress.address}</>}
                          {recommendedShippingAddress.line2 && <>,&#160;{recommendedShippingAddress.line2}</>}
                          {recommendedShippingAddress.cityValue && <><br />{recommendedShippingAddress.cityValue}</>}
                          {recommendedShippingAddress.stateValue && <>,&#160;{recommendedShippingAddress.stateValue}</>}
                          {recommendedShippingAddress.pincode && <>&#160;{recommendedShippingAddress.pincode}</>}
                          {recommendedShippingAddress.countryName && <><br />{recommendedShippingAddress.countryName}</>}
                        </span>
                      </span>
                    }
                  />
                </div>
              )}
            </div>
          </fieldset>
        </div>
        {!formShippingAddress.line2 &&
          addressValidationResult?.result?.address?.unconfirmedComponentTypes
            ?.length > 0 &&
          recommendedShippingAddress &&
          !addressValidationResult?.result?.address?.unconfirmedComponentTypes?.every(
            (type: string) =>
              type === "administrative_area_level_3" ||
              type === "administrative_area_level_4" ||
              type === "administrative_area_level_5"
          ) && (
            <div className="card missingInputs">
              <h4 aria-label={t("MISSING")}>{t("MISSING")}</h4>
              <div id="missing-component-types">
                <ul>
                  {addressValidationResult?.result?.address?.unconfirmedComponentTypes?.map(
                    (componentType, index) => {
                      // Skip certain administrative area levels
                      if (
                        componentType === "administrative_area_level_3" ||
                        componentType === "administrative_area_level_4" ||
                        componentType === "administrative_area_level_5"
                      ) {
                        return null; // Explicitly return null for skipped items
                      }

                      const translationKey =
                        componentType === "postal_code"
                          ? "ZIP_CODE"
                          : componentType === "postal_code_suffix"
                            ? "POSTAL_CODE_SUFFIX"
                            : componentType === "point_of_interest"
                              ? "POINT_OF_INTEREST"
                              : componentType === "subpremise"
                                ? "SUBPREMISE"
                                : componentType === "premise"
                                  ? "PREMISE"
                                  : componentType === "country"
                                    ? "COUNTRY"
                                    : componentType === "route"
                                      ? "ROUTE"
                                      : componentType === "locality"
                                        ? "LOCALITY"
                                        : componentType === "street_number"
                                          ? "STREET_NUMBER"
                                          : componentType === "administrative_area_level_1"
                                            ? "STATE"
                                            : componentType === "administrative_area_level_2"
                                              ? "CITY"
                                              : componentType;

                      return <li key={index} aria-label={t(translationKey)}>{t(translationKey)}</li>;
                    }
                  )}
                </ul>
              </div>
            </div>
          )}
      </Modal.Body>
      <Modal.Footer>
        <h2 aria-label={t('NOTE_TEXT')}>
          {t('NOTE_TEXT')}
        </h2>
        <ul>
          <li aria-label={t('NOTE_1')}>
            <span>{t('NOTE_1')}</span>
          </li>
          {locale !== 'de-de' && locale !== 'es-es' && locale !== 'fr-fr' && locale !== 'it-it' && locale !== 'en-eu' && <li aria-label={t('NOTE_2')}>
            <span>
              {t('NOTE_2')}
            </span>
          </li>}
        </ul>
        <div className="button-container">
          {selectedAddressOption === "yourAddress" && (
            <Button
              variant="success-outline"
              className="btn-green-outline"
              onClick={onShippingAddressUpdate}
              aria-label={t('UPDATE_ADDRESS_BUTTON')}
            >
              {t('UPDATE_ADDRESS_BUTTON')}
            </Button>
          )}
          <Button
            variant="success"
            className="btn-green"
            onClick={(e) => onConfirmAddress(e)}
            aria-label={t('CONFIRM_ADDRESS_BUTTON')}
            autoFocus
          >
            {t('CONFIRM_ADDRESS_BUTTON')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddressModal;
